import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomerContacts, saveCustomerContacts, type CustomerContacts, type CustomerContact } from '../api/client';

export default function CustomerContactsEditor({ customerId }: { customerId: number }) {
  const { t } = useTranslation();
  const [data, setData] = useState<CustomerContacts | null>(null);
  const [draft, setDraft] = useState<CustomerContact[] | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null);
    setError(false);
    getCustomerContacts(customerId).then(({ data: result }) => {
      if (active) setData(result);
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [customerId, revision]);
  const save = async () => {
    if (!data || !draft || busy) return;
    setBusy(true);
    setError(false);
    try {
      const response = await saveCustomerContacts(customerId, { version: data.version, contacts: draft });
      setData(response.data);
      setDraft(null);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="space-y-3 rounded-xl border border-gray-200 p-4" data-testid="customer-contacts">
      <h3 className="font-semibold">{t('collectionWorkflow.contacts')}</h3>
      <p className="text-xs text-gray-500">{t('collectionWorkflow.primaryPreserved')}</p>
      {error && <div role="alert" className="text-sm text-red-700">
        {t('collectionWorkflow.contactsError')}
        <button type="button" className="ms-2 underline" onClick={() => { setDraft(null); setRevision(value => value + 1); }}>{t('common.retry', 'Retry')}</button>
      </div>}
      {!data && !error && <p>{t('common.loading')}</p>}
      {(draft || data?.contacts || []).map((contact, index) => (
        <div key={contact.contact_id} className="flex flex-wrap items-center gap-2">
          {draft ? <>
            {(['name', 'role', 'phone'] as const).map(field => (
              <input key={field} type={field === 'phone' ? 'tel' : 'text'}
                disabled={busy}
                aria-label={t(`collectionWorkflow.${field}`)} value={contact[field]}
                className="min-w-0 rounded border px-2 py-1 text-sm"
                onChange={event => setDraft(draft.map((item, row) => row === index ? { ...item, [field]: event.target.value } : item))} />
            ))}
            <button type="button" disabled={busy} onClick={() => setDraft(draft.filter(item => item.contact_id !== contact.contact_id))}>{t('common.remove', 'Remove')}</button>
          </> : <span className="text-sm">{contact.name} {contact.role && `(${contact.role})`} <bdi>{contact.phone}</bdi></span>}
        </div>
      ))}
      {data && (draft ? <div className="flex gap-3">
        <button type="button" disabled={busy || draft.some(contact => !contact.name.trim() || !contact.phone.trim())} onClick={save} className="rounded bg-blue-600 px-3 py-1.5 text-white">{t('common.save')}</button>
        <button type="button" disabled={busy} onClick={() => setDraft(null)}>{t('common.cancel')}</button>
        <button type="button" disabled={busy || draft.length >= 50} onClick={() => setDraft([...draft, { contact_id: crypto.randomUUID(), name: '', role: '', phone: '' }])}>{t('collectionWorkflow.addContact')}</button>
      </div> : <button type="button" onClick={() => setDraft(data.contacts.length ? data.contacts : [{ contact_id: crypto.randomUUID(), name: '', role: '', phone: '' }])} className="text-sm font-medium text-blue-700">{t('collectionWorkflow.editContacts')}</button>)}
    </section>
  );
}
